import React, { Fragment, useContext, useState } from 'react';
import moment from 'moment';
import { getUpsellCmsKeyPath } from 'Components/utils/activities/activities.utils';
import { isUpsellBooked } from 'Components/utils/booking/bookingUtils';
import { ServicesContext } from 'Context/ServicesContext';
import { camelToSnakeCase } from 'Components/utils/string/string.util';
import logger from 'Logger';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { PopInReservationBlock } from 'Components/widgets/reservation-block/PopInReservationBlock';
type Props = {
  children: (arg0: {
    currentPrice: ExtendedPrice | null | undefined;
    isAdded: boolean;
    isLoading: boolean;
    isUnavailable: boolean;
    onExpand?: (isExpanded: boolean) => void;
    onSubmit: (() => void) | null | undefined;
  }) => React.ReactNode;
  onBook?: ((id: ActivityId | RentalId) => void) | null | undefined;
  upsell: ActivityEntity | RentalEntity;
};
export const UpsellBooking = ({ children, onBook, upsell }: Props) => {
  const [isPopinOpen, setIsPopinOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { dates, updateService } = useContext(ServicesContext);
  const { trackClickEvent } = useTrackEvent();
  const { additionalService, id, openingDates, price, upsellType } = upsell;
  const keyPath = getUpsellCmsKeyPath(additionalService?.pricingInfo?.type);
  const isInBookingMode = Boolean(updateService);
  const currentPrice = isInBookingMode
    ? additionalService?.pricingInfo?.startingPricePerAdult
    : price;
  const isAdded = isUpsellBooked(additionalService);
  const onSubmit = additionalService
    ? () => {
        setIsPopinOpen(true);
        onBook && onBook(id);
      }
    : null;
  const isAvailable =
    openingDates?.length && dates
      ? openingDates.some(({ endDate, startDate }) => {
          return (
            moment(dates.arrivalDate).isBetween(moment(startDate), moment(endDate), 'day') ||
            moment(dates.departureDate).isBetween(moment(startDate), moment(endDate), 'day')
          );
        })
      : true;

  const onExpand = (isExpanded) => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACTIVITIES_LAYER, {
      detail_click: isExpanded ? 'close_arrow' : 'open_arrow',
      activity_type: camelToSnakeCase(upsellType),
      availability: isAvailable || null,
    });
  };

  return (
    <Fragment>
      {children({
        currentPrice,
        isAdded,
        isLoading,
        isUnavailable: !isAvailable,
        onExpand,
        onSubmit,
      })}
      {additionalService && isPopinOpen && (
        <PopInReservationBlock
          headerLabel={additionalService.productInfo?.label}
          introductionLabel={keyPath ? t(`booking.activities.${keyPath}.registration`) : ''}
          onClose={() => setIsPopinOpen(false)}
          onSubmit={async (updatedService, serviceIds, attendeeIds) => {
            setIsLoading(true);

            try {
              await updateService(
                updatedService,
                additionalService.pricingInfo,
                serviceIds,
                attendeeIds,
                camelToSnakeCase(upsellType),
              );
            } catch (e) {
              logger.error(e);
            }

            setIsLoading(false);
          }}
          rentButtonLabel={t('booking.common.addPrice')}
          service={additionalService.pricingInfo}
        />
      )}
    </Fragment>
  );
};
